/**
 * iter-372 — Find & Replace toolbar for Edit BOQ (Ctrl+F / Ctrl+H).
 *
 * Excel-style search across editable line-item fields:
 *   task_title, description, remarks, main_category_name, sub_category_name.
 *
 * Modes:
 *   - Find: highlights all rows containing the query; up/down cycles hits.
 *   - Replace: swaps ALL occurrences of the query with the replacement
 *     across the same fields (respecting case-sensitivity toggle).
 *
 * Parent contract:
 *   items      → current editItems array
 *   onReplaceAll(patches) — patches: [{ actualIndex, patch: {...} }]
 *   Parent applies patches via setEditItems mapping.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, Replace, ChevronDown, ChevronUp, X, CaseSensitive } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const SEARCH_FIELDS = [
  'task_title', 'description', 'remarks',
  'main_category_name', 'sub_category_name',
];

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function useAnchorBottom(open, anchorSelector) {
  const [top, setTop] = useState(64);
  useEffect(() => {
    if (!open || !anchorSelector) return undefined;
    const el = document.querySelector(anchorSelector);
    if (!el) return undefined;
    const zoomOf = (n) => parseFloat(getComputedStyle(n).zoom) || 1;
    const update = () => {
      const z = zoomOf(document.documentElement) * zoomOf(document.body);
      setTop(Math.max(8, Math.round(el.getBoundingClientRect().bottom / z) + 8));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    el.addEventListener('transitionend', update);
    const poll = setInterval(update, 250);
    return () => {
      clearInterval(poll);
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      el.removeEventListener('transitionend', update);
    };
  }, [open, anchorSelector]);
  return top;
}

export default function FindReplaceBar({ open, onOpenChange, items = [], onReplaceAll, onUndo, undoAvailable, onQueryChange, anchorSelector }) {
  const anchorTop = useAnchorBottom(open, anchorSelector);
  const [findVal, setFindVal] = useState('');
  const [replaceVal, setReplaceVal] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [mode, setMode] = useState('find'); // 'find' | 'replace'
  const [hitIdx, setHitIdx] = useState(0);
  const findInputRef = useRef(null);

  // iter-380 — broadcast current query + case flag to the parent so it
  // can propagate visual highlights into BOQ rows and the Annexure grid.
  // Empty query clears the highlight everywhere.
  useEffect(() => {
    if (!onQueryChange) return;
    if (!open) { onQueryChange('', false); return; }
    onQueryChange(findVal, caseSensitive);
  }, [findVal, caseSensitive, open, onQueryChange]);

  // Compute hit list — array of { actualIndex, field, snippet, place }.
  // iter-380: emit a paired "annexure" hit whenever a description
  // contains a match, so pressing Next/Prev cycles through both the BOQ
  // row and the Annexure row for the same item — the two places the
  // match is visible.
  const hits = useMemo(() => {
    if (!findVal.trim()) return [];
    const flags = caseSensitive ? 'g' : 'gi';
    const re = new RegExp(escapeRegex(findVal), flags);
    const out = [];
    (items || []).forEach((it, idx) => {
      if (!it || it.item_type === 'header') return;
      let matched = false;
      let matchedInDescription = false;
      for (const f of SEARCH_FIELDS) {
        const val = String(it[f] || '');
        if (val && re.test(val)) {
          if (!matched) {
            out.push({ actualIndex: idx, field: f, place: 'boq', itemId: it.id || it._editId });
            matched = true;
          }
          if (f === 'description') matchedInDescription = true;
        }
      }
      // Annexure sits below the BOQ table and re-parses the description,
      // so a description match is guaranteed to appear there too.
      if (matched && matchedInDescription) {
        out.push({ actualIndex: idx, field: 'annexure', place: 'annexure', itemId: it.id || it._editId });
      }
    });
    return out;
  }, [findVal, items, caseSensitive]);

  useEffect(() => {
    if (open) setTimeout(() => findInputRef.current?.focus(), 40);
  }, [open, mode]);

  useEffect(() => { setHitIdx(0); }, [findVal, caseSensitive]);

  // Scroll the current hit into view + focus its title input if we can find it.
  // iter-380: when the current hit is an annexure companion, scroll to
  // the annexure item row (`annexure-item-row-<itemId>`) instead of the
  // BOQ title input so users don't have to hunt for it.
  useEffect(() => {
    if (!open || hits.length === 0) return;
    const target = hits[hitIdx];
    if (!target) return;
    let node = null;
    if (target.place === 'annexure' && target.itemId) {
      node = document.querySelector(`[data-testid="annexure-item-row-${target.itemId}"]`);
    }
    if (!node) {
      node = document.querySelector(`[data-testid="item-title-input-${target.actualIndex}"]`);
    }
    if (node) {
      node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      // Add a temporary highlight ring
      const row = node.closest('tr');
      if (row) {
        row.classList.add('ring-2', 'ring-amber-400', 'ring-inset');
        setTimeout(() => row.classList.remove('ring-2', 'ring-amber-400', 'ring-inset'), 1600);
      }
    }
  }, [hitIdx, hits, open]);

  const nextHit = () => { if (hits.length) setHitIdx((i) => (i + 1) % hits.length); };
  const prevHit = () => { if (hits.length) setHitIdx((i) => (i - 1 + hits.length) % hits.length); };

  const doReplaceAll = () => {
    if (!findVal) { toast.error('Enter a search term'); return; }
    const flags = caseSensitive ? 'g' : 'gi';
    const re = new RegExp(escapeRegex(findVal), flags);
    const patches = [];
    let touched = 0;
    let replacements = 0;
    (items || []).forEach((it, idx) => {
      if (!it || it.item_type === 'header') return;
      const patch = {};
      let changed = false;
      for (const f of SEARCH_FIELDS) {
        const val = String(it[f] || '');
        if (!val) continue;
        const newVal = val.replace(re, replaceVal);
        if (newVal !== val) {
          patch[f] = newVal;
          changed = true;
          replacements += (val.match(re) || []).length;
        }
      }
      if (changed) { patches.push({ actualIndex: idx, patch }); touched += 1; }
    });
    if (!touched) { toast.info('No matches found to replace'); return; }
    onReplaceAll?.(patches);
    toast.success(`Replaced ${replacements} occurrence${replacements !== 1 ? 's' : ''} across ${touched} row${touched !== 1 ? 's' : ''}`);
  };

  if (!open) return null;
  return createPortal(
    <div style={{ top: anchorTop }} className="fixed right-6 z-[45] bg-white border border-stone-300 shadow-lg rounded-lg p-3 w-[520px] max-w-[calc(100vw-3rem)]" data-testid="find-replace-bar">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMode('find')}
            className={`px-2 py-1 text-xs rounded ${mode === 'find' ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'text-stone-600 hover:bg-stone-100'}`}
            data-testid="find-replace-mode-find"
          >
            <Search size={12} className="inline mr-1" /> Find
          </button>
          <button
            onClick={() => setMode('replace')}
            className={`px-2 py-1 text-xs rounded ${mode === 'replace' ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'text-stone-600 hover:bg-stone-100'}`}
            data-testid="find-replace-mode-replace"
          >
            <Replace size={12} className="inline mr-1" /> Replace
          </button>
        </div>
        <button
          onClick={() => onOpenChange?.(false)}
          className="p-1 text-stone-400 hover:bg-stone-100 rounded"
          data-testid="find-replace-close-btn"
          title="Close (Esc)"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex items-center gap-1 mb-2">
        <Input
          ref={findInputRef}
          value={findVal}
          onChange={(e) => setFindVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.shiftKey ? prevHit() : nextHit(); }
            else if (e.key === 'Escape') { onOpenChange?.(false); }
          }}
          placeholder="Find in titles, descriptions, remarks, categories…"
          className="h-8 text-sm flex-1"
          data-testid="find-replace-find-input"
        />
        <button
          onClick={() => setCaseSensitive((v) => !v)}
          className={`p-1.5 rounded text-xs ${caseSensitive ? 'bg-amber-100 text-amber-800' : 'text-stone-500 hover:bg-stone-100'}`}
          title="Match case"
          data-testid="find-replace-case-toggle"
        >
          <CaseSensitive size={14} />
        </button>
        <span className="text-[11px] text-stone-500 whitespace-nowrap w-16 text-right">
          {hits.length ? `${hitIdx + 1} / ${hits.length}` : findVal ? '0 / 0' : ''}
        </span>
        {/* iter-380 — location badge tells the user whether the current
            match is in the BOQ row or has scrolled into the Annexure. */}
        {hits.length > 0 && hits[hitIdx] && (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${hits[hitIdx].place === 'annexure' ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}
            data-testid="find-replace-hit-location"
            title={hits[hitIdx].place === 'annexure' ? 'Currently scrolled to the Annexure row' : 'Currently on the BOQ table row'}
          >
            {hits[hitIdx].place === 'annexure' ? 'Annexure' : 'BOQ'}
          </span>
        )}
        <Button size="sm" variant="ghost" onClick={prevHit} className="h-8 px-2" disabled={!hits.length} data-testid="find-replace-prev-btn">
          <ChevronUp size={14} />
        </Button>
        <Button size="sm" variant="ghost" onClick={nextHit} className="h-8 px-2" disabled={!hits.length} data-testid="find-replace-next-btn">
          <ChevronDown size={14} />
        </Button>
      </div>

      {mode === 'replace' && (
        <div className="flex items-center gap-1">
          <Input
            value={replaceVal}
            onChange={(e) => setReplaceVal(e.target.value)}
            placeholder="Replace with…"
            className="h-8 text-sm flex-1"
            data-testid="find-replace-replace-input"
            onKeyDown={(e) => { if (e.key === 'Enter') doReplaceAll(); }}
          />
          <Button size="sm" onClick={doReplaceAll} className="h-8 bg-amber-600 hover:bg-amber-700 text-white" data-testid="find-replace-replace-all-btn">
            Replace All
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { onUndo?.(); }}
            disabled={!undoAvailable}
            className="h-8"
            data-testid="find-replace-undo-btn"
            title="Undo the last Replace All"
          >
            Undo
          </Button>
        </div>
      )}
    </div>,
    document.body
  );
}
